import { Scene, SceneType } from 'types/Scene';
import ImageScenePreview from './scenePreviews/ImageScenePreview';
import VideoScenePreview from './scenePreviews/VideoScenePreview';
import ScenePreview, { EffectPreview } from './scenePreviews/ScenePreview';
import { TransitionType } from 'types/Transitions';
import fadeTransition from './scenePreviews/fadeTransition';
import wipeDiagonalTransition from './scenePreviews/wipeDiagonalTransition';
import { isNil } from 'ramda';
import shapeTransition from './scenePreviews/shapeTransition';
import { updateTransitionList } from './scenePreviews/transitionFrames';

export { default as ScenePreview } from './scenePreviews/ScenePreview';

const getEffect = async (
  transition: Scene['transition'],
  prevTransition: Scene['prevTransition']
): Promise<EffectPreview | null> => {
  let effect: EffectPreview = null;

  if (transition.type === TransitionType.Fade) effect = fadeTransition(transition.duration);
  if (transition.type === TransitionType.WipeDiagonal) effect = wipeDiagonalTransition(transition.duration);
  if (transition.type === TransitionType.Shape) {
    effect = shapeTransition(false, transition, transition.duration / 2);
  }
  if (prevTransition?.type === TransitionType.Shape) {
    const tmpEffect = shapeTransition(true, prevTransition, prevTransition.duration / 2);

    if (effect) {
      const { afterFrameDrawn } = effect;
      effect.afterFrameDrawn = (ctx, preview) => {
        afterFrameDrawn(ctx, preview);
        tmpEffect.afterFrameDrawn(ctx, preview);
      };
    } else {
      effect = tmpEffect;
    }
  }

  return effect;
};

const createScenePreview = async ({
  localSrc,
  duration,
  start,
  type,
  transition,
  trimStart,
  prevTransition,
  scalingMode,
  id
}: Scene): Promise<ScenePreview> => {
  const settings = {
    src: localSrc,
    start,
    duration,
    trimStart,
    scalingMode,
    isPlaying: false,
    type,
    id
  };

  let scene;
  if (type === SceneType.VIDEO) scene = new VideoScenePreview(settings);
  else scene = new ImageScenePreview(settings);

  const effect = await getEffect(transition, prevTransition);
  if (effect) scene.attachEffect(effect);

  return scene;
};

export const createScenePreviews = async (scenes: Scene[]): Promise<ScenePreview[]> => {
  await updateTransitionList(scenes.map(item => item.transition));

  return Promise.all(
    scenes
      .filter(({ type, localSrc }) => type !== SceneType.EMPTY && !isNil(localSrc))
      .reverse()
      .map(scene => createScenePreview(scene))
  );
};
